import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { db, storage } from "../config/firebaseConfig";
import {
  doc,
  getDoc,
  onSnapshot,
  serverTimestamp,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import {
  getDownloadURL,
  ref as storageRef,
  uploadBytes,
  deleteObject,
} from "firebase/storage";
import { Trash2, Upload, Loader, Eye, EyeOff } from "lucide-react";

export default function ManageUpdatesPopup() {
  const navigate = useNavigate();
  const auth = getAuth();

  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);

  const [popupData, setPopupData] = useState({
    popupImageUrl: "",
    popupImageFileName: "",
    uploadedAt: "",
    uploadedBy: "",
    isEnabled: true,
  });

  const [dataLoading, setDataLoading] = useState(true);
  const [saveState, setSaveState] = useState({
    saving: false,
    success: "",
    error: "",
  });

  // Auth check
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(
      auth,
      async (currentUser) => {
        if (currentUser) {
          setUser(currentUser);
          const userRef = doc(db, "users", currentUser.uid);
          const userSnap = await getDoc(userRef);
          const isAdminOrPrincipal =
            userSnap.exists() &&
            (userSnap.data()?.roles?.includes("admin") ||
              userSnap.data()?.roles?.includes("principal") ||
              userSnap.data()?.roles?.includes("ir"));
          setIsAdmin(isAdminOrPrincipal);

          if (!isAdminOrPrincipal) {
            navigate("/");
          }
        } else {
          setUser(null);
          setIsAdmin(false);
          navigate("/");
        }
        setAuthLoading(false);
      },
      [auth, navigate],
    );

    return () => unsubscribe();
  }, [auth, navigate]);

  // Load popup data from Firestore
  useEffect(() => {
    const popupRef = doc(db, "siteSettings", "updatesPopup");

    const unsubscribe = onSnapshot(
      popupRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          setPopupData({
            popupImageUrl: data.popupImageUrl || "",
            popupImageFileName: data.popupImageFileName || "",
            uploadedAt: data.uploadedAt || "",
            uploadedBy: data.uploadedBy || "",
            isEnabled: data.isEnabled !== false,
          });
        } else {
          setPopupData({
            popupImageUrl: "",
            popupImageFileName: "",
            uploadedAt: "",
            uploadedBy: "",
            isEnabled: true,
          });
        }
        setDataLoading(false);
      },
      () => {
        setDataLoading(false);
      },
    );

    return () => unsubscribe();
  }, []);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const isImage = file.type.startsWith("image/");

    if (!isImage) {
      setSaveState({
        saving: false,
        success: "",
        error: "Please select a valid image file.",
      });
      return;
    }

    setSaveState({ saving: true, success: "", error: "" });

    try {
      const safeFileName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
      const filePath = `popup-images/${Date.now()}-${safeFileName}`;
      const mediaRef = storageRef(storage, filePath);

      await uploadBytes(mediaRef, file);
      const fileUrl = await getDownloadURL(mediaRef);

      // Delete old image from storage if it exists
      if (
        popupData.popupImageUrl &&
        popupData.popupImageUrl.includes("storage.googleapis.com")
      ) {
        try {
          const oldFileRef = storageRef(storage, popupData.popupImageUrl);
          await deleteObject(oldFileRef);
        } catch (storageError) {
          console.warn("Failed to delete old image:", storageError);
        }
      }

      // Update Firestore
      await setDoc(
        doc(db, "siteSettings", "updatesPopup"),
        {
          popupImageUrl: fileUrl,
          popupImageFileName: file.name,
          uploadedAt: serverTimestamp(),
          uploadedBy: user?.uid || null,
          isEnabled: true,
        },
        { merge: true },
      );

      setSaveState({
        saving: false,
        success: "Popup image uploaded successfully!",
        error: "",
      });
    } catch (error) {
      console.error("Upload error:", error);
      setSaveState({
        saving: false,
        success: "",
        error: "Upload failed. Please try again.",
      });
    } finally {
      e.target.value = "";
    }
  };

  const handleDeleteImage = async () => {
    if (
      !window.confirm(
        "Are you sure you want to delete this popup image? This action cannot be undone.",
      )
    ) {
      return;
    }

    setSaveState({ saving: true, success: "", error: "" });

    try {
      // Delete from storage
      if (popupData.popupImageUrl.includes("storage.googleapis.com")) {
        try {
          const fileRef = storageRef(storage, popupData.popupImageUrl);
          await deleteObject(fileRef);
        } catch (storageError) {
          console.warn("Failed to delete from storage:", storageError);
        }
      }

      // Clear from Firestore
      await updateDoc(doc(db, "siteSettings", "updatesPopup"), {
        popupImageUrl: "",
        popupImageFileName: "",
        uploadedAt: null,
        uploadedBy: null,
      });

      setSaveState({
        saving: false,
        success: "Popup image deleted successfully!",
        error: "",
      });
    } catch (error) {
      console.error("Delete error:", error);
      setSaveState({
        saving: false,
        success: "",
        error: "Failed to delete image. Please try again.",
      });
    }
  };

  const togglePopupVisibility = async () => {
    setSaveState({ saving: true, success: "", error: "" });

    try {
      await updateDoc(doc(db, "siteSettings", "updatesPopup"), {
        isEnabled: !popupData.isEnabled,
        updatedAt: serverTimestamp(),
      });

      setSaveState({
        saving: false,
        success: `Popup ${!popupData.isEnabled ? "enabled" : "disabled"} successfully!`,
        error: "",
      });
    } catch (error) {
      console.error("Toggle error:", error);
      setSaveState({
        saving: false,
        success: "",
        error: "Failed to update visibility. Please try again.",
      });
    }
  };

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader className="animate-spin" size={40} />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-gray-600">
            You do not have permission to access this page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            Manage Updates & Popup
          </h1>
          <p className="text-gray-600">
            Manage the popup image displayed on the home page alongside updates.
          </p>
        </div>

        {/* Status Messages */}
        {saveState.success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
            {saveState.success}
          </div>
        )}
        {saveState.error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {saveState.error}
          </div>
        )}

        {/* Main Content */}
        <div className="bg-white rounded-lg shadow">
          {dataLoading ? (
            <div className="p-12 flex items-center justify-center">
              <Loader className="animate-spin" size={40} />
            </div>
          ) : (
            <div className="p-8">
              {/* Visibility Toggle */}
              <div className="mb-8 pb-8 border-b">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900 mb-1">
                      Popup Visibility
                    </h2>
                    <p className="text-gray-600 text-sm">
                      {popupData.isEnabled
                        ? "The popup is currently visible on the home page"
                        : "The popup is currently hidden"}
                    </p>
                  </div>
                  <button
                    onClick={togglePopupVisibility}
                    disabled={saveState.saving}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg transition-colors"
                    style={{
                      backgroundColor: popupData.isEnabled
                        ? "#dcfce7"
                        : "#f5f5f5",
                      color: popupData.isEnabled ? "#166534" : "#666",
                      border: `2px solid ${
                        popupData.isEnabled ? "#bbf7d0" : "#e5e5e5"
                      }`,
                    }}
                  >
                    {popupData.isEnabled ? (
                      <>
                        <Eye size={20} />
                        <span>Visible</span>
                      </>
                    ) : (
                      <>
                        <EyeOff size={20} />
                        <span>Hidden</span>
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Current Image Display */}
              {popupData.popupImageUrl && (
                <div className="mb-8 pb-8 border-b">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">
                    Current Popup Image
                  </h2>
                  <div className="bg-gray-50 rounded-lg p-4 mb-4">
                    <img
                      src={popupData.popupImageUrl}
                      alt="Current Popup"
                      className="w-full max-h-96 object-contain rounded-lg"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600 mb-4">
                    <div>
                      <span className="font-semibold">File Name:</span>{" "}
                      {popupData.popupImageFileName}
                    </div>
                    {popupData.uploadedAt && (
                      <div>
                        <span className="font-semibold">Uploaded:</span>{" "}
                        {new Date(
                          popupData.uploadedAt.seconds * 1000,
                        ).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Upload Section */}
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  {popupData.popupImageUrl
                    ? "Replace Popup Image"
                    : "Upload Popup Image"}
                </h2>

                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8">
                  <div className="flex flex-col items-center justify-center">
                    <Upload className="w-12 h-12 text-gray-400 mb-4" />
                    <label className="cursor-pointer">
                      <span className="text-blue-600 font-semibold hover:text-blue-700">
                        Click to upload
                      </span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleFileSelect}
                        disabled={saveState.saving}
                        className="hidden"
                      />
                    </label>
                    <p className="text-gray-500 text-sm mt-2">
                      PNG, JPG, GIF up to 10MB
                    </p>
                  </div>
                </div>
              </div>

              {/* Delete Button */}
              {popupData.popupImageUrl && (
                <div className="mt-8 pt-8 border-t">
                  <button
                    onClick={handleDeleteImage}
                    disabled={saveState.saving}
                    className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-700 border border-red-200 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50"
                  >
                    <Trash2 size={20} />
                    Delete Image
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer Info */}
        <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg text-blue-700 text-sm">
          <strong>Note:</strong> The Updates component displays notices from
          your Firestore database. This page manages only the popup image. To
          manage updates/notices, please use the Notices management section.
        </div>
      </div>
    </div>
  );
}
